import { FC, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  HiOutlineDocumentText, 
  HiOutlinePhotograph, 
  HiOutlineMap, 
  HiOutlineCurrencyDollar, 
  HiOutlineCalendar 
} from 'react-icons/hi';
import { CustomEditor, ImageUpload, Input, SelectInput } from '../../../../commons';
import SwitchInput from '../../../../commons/SwitchInput';
import { useCountries } from '../../../../../hooks';
import Itinary from './itinaries';
import useCountryCategories from '../../../../../hooks/useCountryCategories';
import ScheduledGroupTours from './schedules';
import { DAV_ROLES } from '../../../../../constants';

interface NewTourFormProps {
  values: any;
  setFieldValue: any;
  edit?: boolean;
}
const TourForm: FC<NewTourFormProps> = ({ values, setFieldValue, edit }) => {
  const { user } = useSelector((state: any) => state.auth);
  const [initialValue, setInitialValue] = useState(values.description);
  const { countries, loading: countryLoading } = useCountries();
  const { categories, loading: categoryLoading } = useCountryCategories(values.country);

  const handleImageChange = (e: any) => {
    const file = e.target.files[0];
    setFieldValue('imageFile', file);

    const image = URL.createObjectURL(file);
    setFieldValue('imageCover', image);
  };

  const filteredCategories = categories.filter(
    (category: any) => category?.country?.name?.toLowerCase() === values.country?.toLowerCase(),
  );

  const getCategoryOptions = () => {
    if (categoryLoading) return [{ label: 'Loading...', value: '' }];
    if (!values.country) return [{ label: 'Please select country first!', value: '' }];
    if (filteredCategories.length === 0)
      return [{ label: 'No categories found for this country!', value: '' }];

    return filteredCategories.map((category: any) => ({
      label: category.name,
      value: category.slug,
    }));
  };

  useEffect(() => {
    setFieldValue('description', initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (values.country && !edit) {
      setFieldValue('category', '');
    }
  }, [values.country]);

  return (
    <div className='space-y-10'>
      {/* Tour Overview Section */}
      <section>
        <div className='flex items-center gap-2 mb-4 text-primary-600 dark:text-primary-400'>
          <HiOutlineDocumentText className='w-5 h-5' />
          <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-100'>Tour Overview</h3>
        </div>
        <div className='space-y-6'>
          <Input type='text' name='title' label='Tour Title' placeholder='e.g., Majestic Safari Adventure' />
          <CustomEditor
            placeholder='Describe the experience...'
            onChange={setInitialValue}
            value={initialValue}
            name='description'
          />
        </div>
      </section>

      {/* Highlights & Media Section */}
      <section>
        <div className='flex items-center gap-2 mb-4 text-primary-600 dark:text-primary-400'>
          <HiOutlinePhotograph className='w-5 h-5' />
          <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-100'>Highlights & Media</h3>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-12 gap-6'>
          <div className='md:col-span-8'>
            <Input
              type='textarea'
              label='Tour Highlights'
              placeholder='• Big 5 Game Drive&#10;• Sunset Boat Cruise&#10;• Cultural Village Visit'
              name='tourActivities'
              rows={8}
              note='List key activities, one per line'
              customClass='h-full'
            />
          </div>
          <div className='md:col-span-4'>
            <div className='h-full flex flex-col'>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2'>Cover Image</label>
              <div className='flex-1 min-h-[200px]'>
                <ImageUpload tourImage={values.imageCover} tourImageHandler={handleImageChange} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logistics Section */}
      <section>
        <div className='flex items-center gap-2 mb-4 text-primary-600 dark:text-primary-400'>
          <HiOutlineMap className='w-5 h-5' />
          <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-100'>Logistics</h3>
        </div>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 bg-gray-50 dark:bg-gray-800/50 p-6 rounded-xl border border-gray-100 dark:border-gray-700'>
          <div className='col-span-1'>
            <SelectInput
              name='country'
              label='Country'
              placeholder='Select Country'
              value={values.country}
              setFieldValue={setFieldValue}
              options={
                countryLoading ?
                  []
                  : [
                      ...countries.map((country: any) => ({
                        label: country.name,
                        value: country.name.toLowerCase(),
                      })),
                    ]
              }
              isLoading={countryLoading}
            />
          </div>
          <div className='col-span-1'>
            <SelectInput 
                name='category' 
                label='Category' 
                placeholder='Select Category'
                value={values.category}
                setFieldValue={setFieldValue}
                options={getCategoryOptions()} 
                isDisabled={!values.country || categoryLoading} 
                isLoading={categoryLoading}
            />
          </div>
          <div className='col-span-1'>
            <Input type='number' name='days' label='Duration (Days)' min={1} placeholder='e.g., 5' />
          </div>
          <div className='col-span-1'>
             <Input type='text' name='price' label='Price (USD)' placeholder='e.g., 1500' />
          </div>
        </div>
      </section>

      {/* Inclusions Section */}
       <section>
        <div className='flex items-center gap-2 mb-4 text-primary-600 dark:text-primary-400'>
          <HiOutlineCurrencyDollar className='w-5 h-5' />
          <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-100'>Pricing Details</h3>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Input
            type='textarea'
            name='priceIncludes'
            label='Price Includes'
            placeholder='• Accommodation&#10;• All Meals&#10;• Transport'
            rows={6}
          />
          <Input
            type='textarea'
            name='priceExcludes'
            label='Price Excludes'
            placeholder='• International Flights&#10;• Visas&#10;• Personal Expenses'
            rows={6}
          />
        </div>
      </section>

      {/* Schedules Section */}
      {/* eslint-disable-next-line no-nested-ternary */}
      {user?.role === DAV_ROLES.SUPER_ADMIN ? (
      <section className='bg-gray-50 dark:bg-gray-800/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 transition-all duration-300 hover:border-primary-200 dark:hover:border-primary-500/30'>
          <div className='flex flex-col md:flex-row md:items-center justify-between gap-6'>
             <div className='flex items-start gap-4'>
                <div className='p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 text-primary-600 dark:text-primary-400 shrink-0'>
                    <HiOutlineCalendar className='w-6 h-6' />
                </div>
                <div>
                   <h3 className='text-lg font-bold text-gray-900 dark:text-white flex items-center gap-3'>
                      Scheduled Group Tours
                      <span className='px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-bold uppercase tracking-wider border border-gray-200 dark:border-gray-700'>
                        Optional
                      </span>
                   </h3>
                   <p className='text-gray-500 dark:text-gray-400 mt-1.5 max-w-xl leading-relaxed'>
                      Enable this to set specific departure dates, manage slot availability, instant bookings, and configure accommodation pricing.
                   </p>
                </div>
             </div>
             
             <div className='shrink-0'>
               <SwitchInput
                  checked={values.enableScheduledTours}
                  onChange={(checked) => setFieldValue('enableScheduledTours', checked)}
               />
             </div>
          </div>
          
          {values.enableScheduledTours && (
             <div className='mt-8 pt-8 border-t border-gray-200 dark:border-gray-700 animate-in fade-in slide-in-from-top-4 duration-500'>
                <ScheduledGroupTours values={values} setFieldValue={setFieldValue} />
             </div>
          )}
      </section>
      ) : null}

      {/* Itinerary Section */}
      <section>
         <div className='flex items-center gap-2 mb-4 text-primary-600 dark:text-primary-400'>
          <HiOutlineCalendar className='w-5 h-5' />
          <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-100'>Daily Itinerary</h3>
        </div>
        <Itinary setFieldValue={setFieldValue} values={values} edit={edit} />
      </section>
    </div>
  );
};

export default TourForm;
